import type { SocialMediaPostData } from "@contracts/shareStop";

const POSTING_GRACE_PERIOD_MS = 90 * 60 * 1000;

export interface SocialPostPublishingIssue {
  text: "Processing" | "Problem Posting" | "Partially Posted";
  color: "gray" | "yellow";
}

/**
 * The backend marks completed publishing attempts as Published even when every network failed.
 * Override that display when at least one network explicitly errored. A Posted result or a
 * post URL counts as success, including older records; an error with a post URL is treated
 * as successful. Mixed success and failure results show Partially Posted immediately.
 * Missing/unknown results alone are not proof of failure.
 *
 * When none succeeded, Processing uses a 90-minute grace period because retry counts are not
 * available to the UI. This is an approximation, not confirmation of an active retry. Measure
 * from the scheduled UTC time, falling back to postedOn; never use updatedDate, since retries
 * would restart the grace period.
 * Without either valid timestamp, show Problem Posting immediately. This only changes the
 * display: automatic retries continue independently and can later turn the post into a success.
 * Returns null when the card should retain its existing status display.
 */
export function getSocialPostPublishingIssue(post: SocialMediaPostData | null | undefined, now: number): SocialPostPublishingIssue | null {
  if (post?.status !== "Published" || post.deletedDate) return null;

  const details = post.postedDetails ?? [];
  const hasSuccess = details.some(detail => detail.status === "Posted" || Boolean(detail.postUrl?.trim()));
  const hasFailure = details.some(detail => detail.status === "Error" && !detail.postUrl?.trim());
  if (!hasFailure) return null;
  if (hasSuccess) return { text: "Partially Posted", color: "yellow" };

  const scheduledAt = Date.parse(post.scheduledToPostAt?.postAtUtc ?? "");
  const attemptedAt = Number.isFinite(scheduledAt) ? scheduledAt : Date.parse(post.postedOn ?? "");
  if (Number.isFinite(attemptedAt) && now < attemptedAt + POSTING_GRACE_PERIOD_MS) {
    return { text: "Processing", color: "gray" };
  }
  return { text: "Problem Posting", color: "yellow" };
}
